import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { OrdersService } from '../../orders.service';
import { finalize, Subscription, take } from 'rxjs';
import { PaginatedResponse } from '../../../common/models/common.model';
import { Transporter } from '../../orders.models';
import { NO_TRANSPORTERS_ALERT_MSG } from '../../../common/helpers/constants';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';
import { Dealer } from '../../../dealers/dealers.model';

@Component({
  selector: 'app-transporters',
  standalone: false,
  templateUrl: './transporters.component.html',
  styleUrl: './transporters.component.scss',
})
export class TransportersComponent implements OnInit, OnDestroy {
  @ViewChild('transporterModal') transporterModal!: TemplateRef<any>;
  transporterForm!: FormGroup;
  selectedTransporter: any = null;
  modalRef!: NgbModalRef;

  protected isDataLoading: boolean = false;
  protected isDataSaving: boolean = false;
  protected transporters: Transporter[] = [];

  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected totalCount: number = 0;

  protected currentPage: number = 1;
  protected readonly pageSize = 10;

  private subscription!: Subscription;

  constructor(
    private ordersService: OrdersService,
    private fb: FormBuilder,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getTransporters();
  }

  private initForm(): void {
    this.transporterForm = this.fb.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      vehicle_number: ['', Validators.required],
      vehicle_type: ['', Validators.required],
      phone_number: ['', Validators.required],
      address_line1: [''],
      address_line2: [''],
      email: ['', [Validators.required, Validators.email]],
      user: [''],
    });
  }

  private getTransporters(): void {
    const params = {
      page: this.currentPage,
    };

    this.isDataLoading = true;
    this.subscription = this.ordersService
      .get(params)
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: (transporters: PaginatedResponse<Transporter>) => {
          this.transporters = transporters.results;
          this.totalCount = transporters.count;
          this.hasNextPage = !!transporters.next;
          this.hasPreviousPage = !!transporters.previous;
        },
      });
  }

  openTransporterModal(transporter: Transporter | null = null): void {
    this.selectedTransporter = transporter;

    if (transporter) {
      this.transporterForm.patchValue(transporter);
    } else {
      this.transporterForm.reset();
    }

    this.modalRef = this.modalService.open(this.transporterModal, {
      size: 'lg',
      backdrop: 'static',
    });
  }

  protected onRemoveTransporter(id: string): void {
    const modalRef = this.modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = 'Delete Transporter';
    modalRef.componentInstance.message = 'Are you sure you want to permanently delete this transporter?';
    modalRef.componentInstance.warning =
      'All transporter-related records and assignments will be lost. This action cannot be undone.';

    modalRef.result.then(() => {
      this.ordersService.delete_transporter(id).subscribe({
        next: () => {
          this.transporters = this.transporters.filter((transporter: Transporter) => {
            return transporter.id !== id;
          });

          this.currentPage = 1;
          this.getTransporters();
        },
      });
    });
  }

  protected onSaveTransporter(modal: NgbModalRef): void {
    if (this.transporterForm.invalid) return;

    const formData = this.transporterForm.value;

    this.isDataSaving = true;

    const request$ = this.selectedTransporter
      ? this.ordersService.update_transporter(this.selectedTransporter.id, formData)
      : this.ordersService.post_transporter(formData);

    this.subscription = request$.pipe(finalize(() => (this.isDataSaving = false))).subscribe({
      next: () => {
        this.currentPage = 1;
        this.getTransporters();
        modal.close();
      },
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.modalRef?.close();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.getTransporters();
  }

  /** The row's number in the whole result set, so page 2 starts at 11. */
  protected rowNumber(index: number): number {
    return (this.currentPage - 1) * this.pageSize + index + 1;
  }

  protected readonly NO_TRANSPORTERS_ALERT_MSG = NO_TRANSPORTERS_ALERT_MSG;
}
